"use client";

import React from "react";
import { MapPin, ShieldCheck, Zap, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from 'react';
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function TheWebsiteShopAdvantage() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const benefits = [{
    title: "Local Central Coast Support",
    description: "We aren't a faceless overseas agency. Based right here in the Central Coast, Australia, we understand the local market. We offer real support for trades and small businesses—painters, landscapers, and electricians who need a partner they can trust.",
    icon: MapPin,
    staggerClass: "lg:ml-0"
  }, {
    title: "No Hidden Fees",
    description: "What you see is what you get. Our fixed-price packages mean you never have to worry about surprise bills. From the Starter package at $995 + GST to our Advanced solutions, pricing is transparent, affordable, and built for small business budgets.",
    icon: ShieldCheck,
    staggerClass: "lg:ml-32"
  }, {
    title: "Lightning-Speed Performance",
    description: "We build websites that feel like modern software. Using the latest tech stack, we ensure your site loads instantly, ranks well on Google, and converts visitors into customers. Fast turnaround times mean you can be online in as little as 14 days.",
    icon: Zap,
    staggerClass: "lg:ml-12"
  }];
  return <section id="the-website-shop-advantage" ref={ref} className="bg-background text-foreground py-[120px] overflow-hidden relative">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-20">
        <div className="absolute top-[10%] left-[5%] w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-[10%] right-[5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
          
          {}
          <div className="lg:col-span-3 flex lg:justify-end lg:items-start">
            <div className={cn("transition-all duration-1000 ease-out transform", isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10")}>
              <h2 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter uppercase text-transparent bg-clip-text bg-gradient-to-b from-foreground to-foreground/40 lg:[writing-mode:vertical-rl] lg:rotate-180 lg:h-[600px] leading-none">
                Why Website Shop?
              </h2>
            </div>
          </div>

          {}
          <div className="lg:col-span-9 flex flex-col gap-20 lg:gap-32 pt-10 lg:pt-0">
            {benefits.map((benefit, i) => <div key={i} data-index={i} className={cn("relative group transition-all duration-1000 ease-out", benefit.staggerClass, isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20")} style={{
            transitionDelay: `${i * 200}ms`
          }}>
                {}
                <Separator className="bg-primary h-[2px] w-16 mb-8 group-hover:w-32 transition-all duration-500 ease-out" />
                
                <div className="flex flex-col md:flex-row gap-6 md:items-start">
                  <div className="shrink-0 p-3 rounded-xl bg-secondary/50 border border-border/50 text-primary">
                    <benefit.icon className="w-8 h-8" />
                  </div>
                  
                  <div className="max-w-xl">
                    <h3 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight text-foreground group-hover:text-primary transition-colors duration-300">
                      {benefit.title}
                    </h3>
                    <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                      {benefit.description}
                    </p>
                    
                    {i === 2 && <div className="mt-8">
                         <Button asChild variant="default" size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-8">
                            <Link to="/PackagesPage">
                                View Packages <ArrowRight className="ml-2 w-4 h-4" />
                            </Link>
                         </Button>
                      </div>}
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}